import React, { memo, useMemo } from 'react';
import * as svg from '../../../constants/ui/svg';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatNumber } from 'utils';
import { SetMenu, SetMenuOption } from 'models';
import { useHistory, useLocation } from 'react-router-dom';
interface ISetTab {
  menuSetList: Array<SetMenu>;
  selectedSetOption: SetMenu;
  setSelectedSetOption: (item: SetMenu) => void;
}
const SetTab: React.FC<ISetTab> = ({ menuSetList, setSelectedSetOption, selectedSetOption }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const router = useLocation();
  const calculateOptionBySetMenu = useMemo(() => {
    let result: Array<any> = [];
    let options = Array.isArray(selectedSetOption.options) ? selectedSetOption.options : [];
    const shouldSort = options.every(
      (option) => option.hasOwnProperty('orderby') && option.orderby !== null
    );
    if (shouldSort) {
      options = [...options!].sort((a, b) => {
        if (a?.orderby === b?.orderby) {
          return a?.type - b?.type;
        } else {
          return a.orderby! - b.orderby!;
        }
      });
    }

    for (let option of options) {
      const found = result.find((i) => i.customer_type === option.customer_type);
      if (!found && option.type === 1) {
        const params = {
          init_price: {},
          customer_type: option.customer_type,
          nomination_fees: option.nomination_fees,
          sub_nomination_fees: option.sub_nomination_fees,
          seat_fees: option.seat_fees,
          seat_fee_money: option.seat_fee_money,
          companion_fees: option.companion_fees,
          free_nomination: option.free_nomination,
          hide_category_product: option.hide_category_product || [],
        };
        if (option.type === 1) {
          params.init_price = {
            price: option.price,
            is_free_time: option.is_free_time === 1 ? true : false,
            per_time: option.per_time,
            consumption_tax: option.consumption_tax === 1 ? true : false,
            tax: option.tax === 1 ? true : false,
            seat_fee_money: option.seat_fee_money,
            hide_category_product: option.hide_category_product || [],
          };
        }
        result.push(params);
      }
    }
    for (let option of options) {
      const index = result.findIndex((i) => i.customer_type === option.customer_type);
      if (index !== -1 && option.type === 2) {
        result[index].extension_price = {
          price: option.price,
          per_time: option.per_time,
          is_free_time: false,
          consumption_tax: option.consumption_tax === 1 ? true : false,
          tax: option.tax === 1 ? true : false,
        };
      }
    }

    result = result.map((res) => {
      res.set_menu =
        selectedSetOption.menu_details?.filter(
          (i) => i.customer_type === res.customer_type && i.content !== '[]'
        ) || [];
      return res;
    });
    return result;
  }, [selectedSetOption]);

  const handleSelectSet = (item: SetMenu) => {
    const searchParams = new URLSearchParams(router.search);
    if (searchParams.has('set_id')) {
      searchParams.set('set_id', String(item.id));
    } else {
      searchParams.append('set_id', String(item.id));
    }
    const newUrl = `${router.pathname}?${searchParams}`;
    history.replace(newUrl);
    setSelectedSetOption(item);
  };
  const parseContentOfSetMenu = (content: string) => {
    if (content && content.includes('[')) {
      let menu = JSON.parse(content);
      menu = menu.map(
        (i: { qty: string; prod: string }) => `${i.qty === 'free' ? t('FREE') : i.qty} ${i.prod}`
      );
      return menu.join(' + ');
    }
    return content;
  };

  const __renderNumberHostFreeInSet = (item: SetMenuOption) => {
    return item.free_nomination > 0
      ? `( ${t('APPOINTEE')} ${formatNumber(item.free_nomination)} ${t('FREE_PERSON')} )`
      : `( ${t('NO_FREE_NOMINATION')} )`;
  };

  const __renderSetMenuPrice = (item: SetMenuOption | any) => (
    <>
      <span className="font-normal text-brown_dark">
        {formatMoney(item.init_price.price) || 0} /{' '}
        <span>
          {item.init_price.is_free_time ? 'FREE TIME' : `${item?.init_price?.per_time || 0}分`}
        </span>
      </span>
      <span className="!text-secondary text-xs font-normal">({t('TAX_INCLUDED')})</span>
    </>
  );
  return (
    <>
      <div className="left-side h-full max-w-[100px] w-full border-r border-border_primary overflow-y-auto">
        {[...menuSetList]
          .sort((a, b) => a.orderby - b.orderby)
          .map((item, index: number) => {
            const isSelected = selectedSetOption.id == item.id;
            return (
              <button
                onClick={() => handleSelectSet(item)}
                key={index}
                className={`border-b border-solid border-border_primary min-h-[60px] p-1 w-full text-center ${
                  isSelected && 'bg-[#EEEEF8] font-bold'
                }`}
              >
                {item.name}
              </button>
            );
          })}
      </div>
      <div className="w-full h-full p-2 space-y-2 overflow-y-auto right-side bg-gray_50">
        {calculateOptionBySetMenu.length ? (
          calculateOptionBySetMenu.map(
            (item, index: number) =>
              item.init_price.price >= 0 && (
                <div
                  key={index}
                  className="p-2 pb-0 bg-white border rounded-md border-border_primary gap-x-2"
                >
                  <div className="flex flex-col justify-between font-[NotoSans]">
                    <span className="text-base font-medium text-brown_dark">
                      {item.customer_type}
                    </span>
                    {/* NUMBER OF HOST FREE IN SET */}
                    <span className="text-xs text-secondary">
                      {__renderNumberHostFreeInSet(item)}
                    </span>
                    <div className="py-2">
                      {/* PRICE */}
                      <div className="flex text-sm font-medium gap-x-2">
                        <span className="flex items-center h-5">{svg.moneyIcon}</span>
                        <div className="flex flex-wrap items-center gap-1">
                          {__renderSetMenuPrice(item)}
                        </div>
                      </div>
                      {/* AUTOMATIC EXTENSION */}
                      {!item.init_price.is_free_time && item.extension_price?.price !== 0 && (
                        <>
                          <div className="flex gap-x-[7px] items-center">
                            <span>{svg.moneyExtensionIcon}</span>
                            <span className="text-brown_dark">{t('AUTOMATIC_EXTENSION')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-4"></span>
                            <div className="space-x-2 text-brown_dark">
                              <span>
                                {formatMoney(item.extension_price?.price)} /{' '}
                                {item.extension_price?.per_time}
                                {t('HOUR')}
                              </span>
                              <span className="text-xs text-secondary">({t('TAX_INCLUDED')})</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    {/* MENU OF SET */}
                    {item.set_menu.length ? (
                      <div className="border-t border-border_primary py-2 flex flex-col gap-y-1 !text-brown_dark">
                        <span className="font-medium">{t('MENU')}</span>
                        {item.set_menu.map((setItem: any, index: number) => (
                          <span key={index}>{parseContentOfSetMenu(setItem.content)}</span>
                        ))}
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              )
          )
        ) : (
          <span className="block w-full py-3 text-center">{t('NO_DATA')}</span>
        )}
      </div>
    </>
  );
};

export default memo(SetTab);
